import React, { createContext, useState, useContext, useEffect } from 'react';
import CookieConsent from "react-cookie-consent";
import AOS from "aos";
import "aos/dist/aos.css";
import "./index.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Import translations
import enTranslations from './locales/en.json';
import itTranslations from './locales/it.json';

// Import components
import Home from "./pages/Home";
import Contact from "./pages/Contact";
import DemoProduct from "./pages/DemoProduct";
import Pricing from './pages/Pricing';
import AuthPage from "./pages/AppAuth";
import Terms from "./pages/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Upgrade from "./pages/dashboard/Upgrade"; 
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import { useDocTitle } from "./components/CustomHook";
import ScrollToTop from "./components/ScrollToTop";
import OffersPage from "./pages/OffersPage";
import AnalysisPage from "./pages/AnalysisPage";
import PaymentPage from "./pages/PaymentPage";
import ConnectBank from "./pages/dashboard/ConnectBank";
import Dashboard from "./pages/Dashboard";
import Marketplace from "./pages/dashboard/Marketplace";
import AccountSettings from "./pages/dashboard/AccountSettings";
import Partners from "./pages/dashboard/Partners";
import Notifications from "./pages/dashboard/Notifications";
import Premium from "./pages/dashboard/Premium";
import Merchant from "./pages/dashboard/Merchant";
import ShareSubs from "./pages/dashboard/ShareSubs";
import VirtualCard from "./pages/dashboard/VirtualCard";
import MarketplaceCategory from "./pages/dashboard/MarketplaceCategory";

import MobileDashboard from "./pages/mobile/MobileDashboard";
import MobileMarketplace from "./pages/mobile/MobileMarketplace";
import MobileMarketplaceCategory from "./pages/mobile/MobileMarketplaceCategory";
import MobileNotification from "./pages/mobile/MobileNotification";
import MobileMerchant from "./pages/mobile/MobileMerchant";
import MobileConnectBank from "./pages/mobile/MobileConnectBank";
import MobileAccountSettings from "./pages/mobile/MobileAccountSettings";
import MobilePartners from "./pages/mobile/MobilePartners";
import MobileShareSubs from "./pages/mobile/MobileShareSubs";
import MobileUpgrade from "./pages/mobile/MobileUpgrade";

import ProtectedRoute from "./utils/ProtectedRoute";

// Language Context
import { LanguageProvider, useLanguage, useTranslation } from "./contexts/LanguageContext";

// Safe status bar setup for mobile (iOS/Android only)
import { StatusBar, Style } from "@capacitor/status-bar";

import PlatformRedirect from "./utils/PlatformRedirect";

// Create AuthContext
const AuthContext = createContext();

// Create useAuth hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Storage service for Capacitor
class StorageService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  async set(key, value) {
    if (this.isNative) {
      await Preferences.set({ key, value: JSON.stringify(value) });
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  async get(key) {
    if (this.isNative) {
      const result = await Preferences.get({ key });
      return result.value ? JSON.parse(result.value) : null;
    } else {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }
  }

  async remove(key) {
    if (this.isNative) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }

  async clear() {
    if (this.isNative) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  }
}

const storageService = new StorageService();

// HTTP client with platform detection
class HttpClient {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.baseURL = 'https://api.easysubs.it';
  }

  async request(endpoint, options = {}) {
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Platform': this.isNative ? 'capacitor' : 'web'
      }
    };

    // Add authorization for native
    if (this.isNative) {
      const token = await storageService.get('authToken');
      if (token) {
        defaultOptions.headers['Authorization'] = `Bearer ${token}`;
      }
    } else {
      // Web uses cookies
      defaultOptions.credentials = 'include';
    }

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      }
    };

    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, mergedOptions);

    // Handle token expiration
    if (response.status === 401) {
      const data = await response.json();
      if (data.code === 'TOKEN_EXPIRED') {
        // Try to refresh token
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry the request with new token
          return this.request(endpoint, options);
        }
      }
      throw new Error(data.error || 'Authentication failed');
    }

    return response;
  }

  async refreshToken() {
    try {
      if (this.isNative) {
        const token = await storageService.get('authToken');
        if (!token) return false;

        const response = await fetch(`${this.baseURL}/api/refresh-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Client-Platform': 'capacitor'
          }
        });

        if (response.ok) {
          const data = await response.json();
          await storageService.set('authToken', data.token);
          return true;
        }
      } else {
        // Web: cookie will be refreshed automatically
        const response = await fetch(`${this.baseURL}/api/refresh-token`, {
          method: 'POST',
          credentials: 'include'
        });
        return response.ok;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }
}

const httpClient = new HttpClient();

// Create AuthProvider component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isNative = Capacitor.isNativePlatform();

  // Native login flow
  const nativeLogin = async (email, password) => {
    try {
      const response = await fetch('https://api.easysubs.it/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Platform': 'capacitor'
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      
      // Store token for native
      if (data.token) {
        await storageService.set('authToken', data.token);
      }
      
      // Store user data
      await storageService.set('user', data.user);
      
      setUser(data.user);
      return data.user;
    } catch (error) {
      throw error;
    }
  };

  // Web login flow
  const webLogin = async (email, password) => {
    try {
      const response = await fetch('https://api.easysubs.it/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      setUser(data.user);
      return data.user;
    } catch (error) {
      throw error;
    }
  };

  // Login function
  const login = async (email, password) => {
    if (isNative) {
      return await nativeLogin(email, password);
    } else {
      return await webLogin(email, password);
    }
  };

  // Native logout for Capacitor
  const nativeLogout = async () => {
    try {
      const token = await storageService.get('authToken');
      if (token) {
        const response = await fetch('https://api.easysubs.it/api/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Client-Platform': 'capacitor'
          }
        });
        
        if (!response.ok) {
          console.warn('Backend logout failed, but continuing with client cleanup');
        }
      }
    } catch (error) {
      console.error('Native logout API error:', error);
      // Continue with cleanup even if API fails
    } finally {
      // Always clear local storage
      await storageService.clear();
      
      // Clear any Capacitor Preferences
      try {
        await Preferences.clear();
      } catch (e) {
        console.warn('Failed to clear Capacitor preferences:', e);
      }
    }
  };

  // Web logout
  const webLogout = async () => {
    try {
      const response = await fetch('https://api.easysubs.it/api/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Client-Platform': 'web'
        }
      });
      
      if (!response.ok) {
        console.warn('Backend logout failed, but continuing with client cleanup');
      }
    } catch (error) {
      console.error('Web logout API error:', error);
      // Continue with cleanup even if API fails
    } finally {
      // Clear localStorage items
      localStorage.removeItem('userID');
      localStorage.removeItem('userName');
      localStorage.removeItem('country');
      localStorage.removeItem('userType');
      
      // Clear auth-related items
      localStorage.removeItem('authToken');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user');
    }
  };

  // Consolidated logout function
  const logout = async () => {
    if (isNative) {
      await nativeLogout();
    } else {
      await webLogout();
    }
    
    // Clear React state
    setUser(null);
    
    // Return success
    return true;
  };

  // Validate native token
  const validateNativeToken = async () => {
    try {
      const token = await storageService.get('authToken');
      if (!token) return null;

      const response = await fetch('https://api.easysubs.it/api/validate-token', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Client-Platform': 'capacitor'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const storedUser = await storageService.get('user');
        const user = data.user || storedUser;
        if (user) {
          setUser(user);
          return user;
        }
      }
      return null;
    } catch (error) {
      console.error('Native token validation error:', error);
      return null;
    }
  };

  // Validate web token
  const validateWebToken = async () => {
    try {
      const response = await fetch('https://api.easysubs.it/api/validate-token', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        return data.user;
      }
      return null;
    } catch (error) {
      console.error('Web token validation error:', error);
      return null;
    }
  };

  // Check auth on initial load
  const validateToken = async () => {
    try {
      let user = null;
      if (isNative) {
        user = await validateNativeToken();
      } else {
        user = await validateWebToken();
      }
      return user;
    } catch (error) {
      console.error('Token validation error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    validateToken();
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      validateToken,
      isAuthenticated: !!user,
      isNative,
      httpClient
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cookie Consent Component with translations (updated for strictly necessary cookies)
const TranslatedCookieConsent = ({ showCookieBanner, handleAcceptCookies }) => {
  const { t } = useTranslation();

  if (!showCookieBanner) return null;

  return (
    <CookieConsent
      location="bottom"
      buttonText={t('cookieConsent.acknowledge')}   // Changed to acknowledgment text
      enableDeclineButton={false}                   // No decline button (essential cookies only)
      flipButtons
      cookieName="easySubsCookieConsent"
      style={{ background: "#2B373B" }}
      buttonStyle={{
        background: "#4CAF50",
        color: "#fff",
        fontSize: "13px",
      }}
      expires={150}
      onAccept={handleAcceptCookies}
      debug={true}
    >
      {t('cookieConsent.message')}{" "}
      <a href="/terms" style={{ color: "#4CAF50" }}>
        {t('cookieConsent.learnMore')}
      </a>
    </CookieConsent>
  );
};

function AppContent() {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Initialize AOS with proper settings (works on all platforms)
    AOS.init({
      duration: 1000,
      easing: 'ease-out-cubic',
      once: true,
      offset: 100,
      delay: 100
    });

    // Refresh AOS after components mount
    setTimeout(() => {
      AOS.refresh();
    }, 1000);

    // Status bar setup (safe to call on web, it checks isNativePlatform internally)
    const setupStatusBar = async () => {
      if (!Capacitor.isNativePlatform()) return;
    
      try {
        // Prevent content from going under status bar
        await StatusBar.setOverlaysWebView({ overlay: false });
        // set style **before** background color
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
        await StatusBar.show();
      } catch (e) {
        console.warn('StatusBar plugin not available:', e);
      }
    };

    setupStatusBar();

    // Cookie consent: only show on web, not on native (iOS/Android)
    if (Capacitor.isNativePlatform()) {
      setShowCookieBanner(false); // Ensure banner is hidden on native
    } else {
      checkCookieConsent(); // Web: check if consent has been given
    }
  }, []);

  const checkCookieConsent = () => {
    const cookieConsent = localStorage.getItem('easySubsCookieConsent');
    // Only show banner if no consent decision has been stored
    setShowCookieBanner(!cookieConsent);
  };

  const handleAcceptCookies = () => {
    // Store consent in localStorage with expiration date (150 days from now)
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 150);
    
    const consentData = {
      accepted: true,
      timestamp: new Date().toISOString(),
      expires: expirationDate.toISOString()
    };
    
    localStorage.setItem('easySubsCookieConsent', JSON.stringify(consentData));
    setShowCookieBanner(false);
    
    // Optional tracking scripts can be added here
  };

  useDocTitle("EasySubs - Subscription Management App");

  return (
    <Router>
      <ScrollToTop>
        {/* Only render cookie banner on web (showCookieBanner will be false on native) */}
        <TranslatedCookieConsent 
          showCookieBanner={showCookieBanner}
          handleAcceptCookies={handleAcceptCookies}
        />

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<><PlatformRedirect /><Home /></>} />
          <Route path="/contact" element={<Contact />} />
          {/*<Route path="/get-demo" element={<DemoProduct />} /> */}
          <Route path="/demo" element={<AuthPage />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/viewAll" element={<AnalysisPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/startDemo" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/connectBank" element={<ConnectBank />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/mobile-dashboard" element={<MobileDashboard />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace-category/:category" element={<MarketplaceCategory />} />
            <Route path="/account-settings" element={<AccountSettings />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/merchant/:merchantId" element={<Merchant />} />
            <Route path="/share-subs" element={<ShareSubs />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/mobile-marketplace" element={<MobileMarketplace />} />
            <Route path="/mobile-marketplace-category/:category" element={<MobileMarketplaceCategory />} />
            <Route path="/mobile-merchant/:merchantId" element={<MobileMerchant />} />
            <Route path="/mobile-notifications" element={<MobileNotification />} />
            <Route path="/mobile-connectBank" element={<MobileConnectBank />} />
            <Route path="/mobile-partners" element={<MobilePartners />} />
            <Route path="/mobile-share-subs" element={<MobileShareSubs />} />
            <Route path="/mobile-account-settings" element={<MobileAccountSettings />} />
            <Route path="/mobile-upgrade" element={<MobileUpgrade />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/virtual-card" element={<VirtualCard />} />
          </Route>
        </Routes>
      </ScrollToTop>
    </Router>
  );
}

function App() {
  const translations = {
    en: enTranslations,
    it: itTranslations
  };

  return (
    <AuthProvider>
      <LanguageProvider translations={translations}>
        <AppContent />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;